export interface Role {
  id: string;
  company_id: string;
  name: string;
  slug: string;
  description: string | null;
  is_default: boolean;
  is_system: boolean;
  primary_manager_role_id: string | null;
  requires_2fa: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface RolePermission {
  id: string;
  role_id: string;
  company_id: string;
  permission_key: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface RoleAuditLog {
  id: string;
  company_id: string;
  role_id: string | null;
  role_name: string | null;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'ASSIGN' | 'UNASSIGN' | 'PERMISSION_CHANGE';
  performed_by: string | null;
  changes: Record<string, unknown> | null;
  affected_user_count: number | null;
  created_at: string;
}

export const PERMISSION_KEYS = {
  SUBMIT_UNSCHEDULED_TIMESHEETS: 'submit_unscheduled_timesheets',
  CHANGE_OWN_SHIFT_TIMES: 'change_own_shift_times',
  CLOCK_WITHOUT_SCHEDULE: 'clock_without_schedule',
  EDIT_OWN_TIMESHEET: 'edit_own_timesheet',
  ROSTER_MANAGER: 'roster_manager',
  ADD_EDIT_SHIFTS: 'add_edit_shifts',
  APPROVE_TIMESHEETS: 'approve_timesheets',
  TIMESHEET_STAGING: 'timesheet_staging',
  EXPORT_TIMESHEETS: 'export_timesheets',
  APPROVE_LEAVE_REQUESTS: 'approve_leave_requests',
  VIEW_EMPLOYEE_DETAILS: 'view_employee_details',
  MANAGE_EMPLOYEE_DETAILS: 'manage_employee_details',
  MANAGE_EMPLOYEE_PAY: 'manage_employee_pay',
  PORTFOLIO_VIEW: 'portfolio_view',
  PORTFOLIO_MANAGE: 'portfolio_manage',
  MANAGE_EMPLOYEE_ROLES: 'manage_employee_roles',
  SYSTEM_CONFIG_VIEW: 'system_config_view',
  SYSTEM_CONFIG_MANAGE: 'system_config_manage',
  CAN_MANAGE_ROLES: 'can_manage_roles',
  RUN_ACCRUALS: 'run_accruals',
} as const;

export type PermissionKey = typeof PERMISSION_KEYS[keyof typeof PERMISSION_KEYS];

export const PERMISSION_CATEGORIES = {
  'Global Applications': ['submit_unscheduled_timesheets', 'change_own_shift_times', 'clock_without_schedule', 'edit_own_timesheet'],
  'Rostering': ['roster_manager', 'add_edit_shifts'],
  'Time Sheets': ['approve_timesheets', 'timesheet_staging', 'export_timesheets'],
  'Leave Management': ['approve_leave_requests', 'run_accruals'],
  'Employee Details': ['view_employee_details', 'manage_employee_details', 'manage_employee_pay', 'manage_employee_roles'],
  'System Configuration': ['system_config_view', 'system_config_manage', 'can_manage_roles'],
  // Portfolio permissions (portfolio_view, portfolio_manage) are reserved for future use
  // and intentionally hidden from UI until the portfolio module is implemented
} as const;

export const PERMISSION_LABELS: Record<string, string> = {
  submit_unscheduled_timesheets: 'Submit manual timesheets',
  change_own_shift_times: 'Change start/end times of own shifts',
  clock_without_schedule: 'Clock in/out without being scheduled',
  edit_own_timesheet: 'Edit own timesheet after clocking out',
  roster_manager: 'Roster Manager',
  add_edit_shifts: 'Add/Edit Shifts',
  approve_timesheets: 'Approve Time Sheets',
  timesheet_staging: 'Time Sheets Staging',
  export_timesheets: 'Export Approved Time Sheets',
  approve_leave_requests: 'Approve Leave Requests',
  view_employee_details: 'View Employee Details',
  manage_employee_details: 'Manage Employee Details',
  manage_employee_pay: 'Manage Employee Pay Details',
  portfolio_view: 'View Portfolio',
  portfolio_manage: 'Manage Portfolio',
  manage_employee_roles: 'Assign roles to employees',
  system_config_view: 'View System Configuration',
  system_config_manage: 'Manage System Configuration',
  can_manage_roles: 'Manage role definitions',
  run_accruals: 'Run Accrual Processing',
};

// Helper text for permissions that relate to other permissions
export const PERMISSION_HINTS: Record<string, string> = {
  submit_unscheduled_timesheets: 'Create manual time entries from the My Timesheets page without needing a scheduled shift.',
  change_own_shift_times: 'Edit the start and end times of your own future shifts from the schedule view.',
  clock_without_schedule: 'Allow clocking in/out even when no shift is scheduled, bypassing the unscheduled check-in restriction.',
  edit_own_timesheet: 'Edit your own timesheet entries after clocking out, as long as they have not been approved.',
  roster_manager: 'Access the Roster Planner to publish, manage, and apply templates to monthly rosters.',
  add_edit_shifts: 'Access the Schedule Planner to create, edit, and delete shifts including open shifts.',
  approve_timesheets: 'Approve or reject employee timesheets. Also enables receiving attendance notifications (missed check-ins, late arrivals, geofence alerts).',
  timesheet_staging: 'Generate payroll runs, view and manage payslips, and rollback completed runs.',
  export_timesheets: 'Export approved timesheet data as CSV from the payroll page. Requires "Approve Time Sheets" to be enabled.',
  approve_leave_requests: 'View, approve, or reject employee leave requests. Access the Leave Management page and leave accrual history.',
  view_employee_details: 'View employee profiles, contact details, and the Team Management page (read-only).',
  manage_employee_details: 'Edit employee profiles, archive and reactivate team members, and access People settings.',
  manage_employee_pay: 'View employee pay rates, manage compensation rules and assignments, and adjust leave balances.',
  manage_employee_roles: 'Apply roles to employees and invite new users with a role. Requires roles to already exist.',
  system_config_view: 'View business settings pages (General, Scheduling, Time & Attendance, Holidays) in read-only mode.',
  system_config_manage: 'Edit all business settings including leave policies, geolocation, communications, shift codes, billing, and notification delivery.',
  can_manage_roles: 'Create, edit, and delete role templates. Does not allow assigning roles to users—enable "Assign roles to employees" for that.',
  run_accruals: 'Trigger hourly/daily/weekly/monthly accrual processing for leave balances.',
};
